<script setup lang="ts">
const prop = withDefaults(
  defineProps<{
    img?: string;
    imgAlt?: string;
    title: string;
    cancelBtnLabel?: string;
    okBtnLabel?: string;
    hideOkBtn?: boolean;
    hideCancelBtn?: boolean;
    hideFooter?: boolean;
    showUpgradeBtn?: boolean;
    closable?: boolean;
    size?: string;
    textCenter?: boolean;
    id?: string;
    busy?: boolean;
    navGuardSave?: boolean;
    dataContentAction?: string;
    dataContentType?: string;
    dataUserActivity?: string;
  }>(),
  {
    cancelBtnLabel: "Cancel",
    okBtnLabel: "Ok",
    hideCancelBtn: false,
    hideOkBtn: false,
    hideFooter: false,
    closable: true,
    size: undefined,
    img: "",
    imgAlt: "",
    textCenter: false,
    id: () => uniqueId("modal"),
    dataContentAction: "",
    dataContentType: "",
    dataUserActivity: "",
  },
);

const enableDOM = ref<boolean>(false);
const modal = ref<HTMLDivElement>();
const modals = useModalStore();

async function modalInit() {
  if (!modal.value) {
    return;
  }
}
onMounted(() => {
  show();
  modals.onModalMount();
  emitter.on("hideAllModals", onEmitterHideAll);
});
onBeforeUnmount(() => {
  emitter.off("hideAllModals", onEmitterHideAll);
});

function onEmitterHideAll() {
  hide();
}
const debug = useDebug();

async function show() {
  enableDOM.value = true;
  await waitUntilTrue(() => !!modal.value);
  await modalInit();
  emit("show");
  debug.log("modal showing");
  //modal.value!.showModal();
  await nextTick();

  if ("activeElement" in document) {
    (document.activeElement as any)?.blur &&
      (document.activeElement as any).blur();
  }
}

function hide() {
  enableDOM.value = false;
  modals.disposeAllModals();
  emit("close");
}

function dontSave() {
  hide();
  emit("dontSave");
}

const emit = defineEmits<{
  (e: "ok"): void;
  (e: "show"): void;
  (e: "close"): void;
  (e: "hidePrevented"): void;
  (e: "dontSave"): void;
}>();

function onOk() {
  emit("ok");
}

const sizeClass = computed(() => (prop.size ? `max-w-${prop.size}` : ""));

defineExpose({ show, hide });
</script>

<template>
  <!-- max-w-xl max-w-2xl max-w-3xl max-w-4xl max-w-5xl max-w-6xl max-w-7xl max-w-sm max-w-xs -->

  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="enableDOM"
        class="modal"
        role="dialog"
        open
      >
        <div
          class="modal-box"
          :class="sizeClass"
        >
          <div class="modal-content">
            <div
              v-if="img"
              class="modal-header-image"
            >
              <Image
                :alt="imgAlt"
                :src="img"
              />
            </div>
            <div class="modal-header">
              <h2 class="modal-title">
                {{ title.charAt(0).toUpperCase() + title.slice(1) }}
              </h2>
              <slot name="header-center" />
              <button
                v-if="closable"
                type="button"
                class="btn-close"
                :class="{ 'btn-close-on-img': !!img }"
                :aria-label="$t('G.LIT_Close')"
                @click="hide"
              >
                <FAIcon class="fa-light fa-xmark text-2xl leading-4" />
              </button>
            </div>
            <div class="modal-body">
              <slot />
            </div>
            <div class="modal-footer">
              <div
                v-if="!hideFooter"
                class="modal-footer-buttons"
              >
                <div class="order-2 md:order-1">
                  <slot name="footer">
                    <div class="flex flex-wrap items-center gap-1">
                      <Button
                        v-if="navGuardSave"
                        type="button"
                        class="btn btn-secondary"
                        variant="secondary"
                        @click="dontSave()"
                      >
                        {{ $t("SV.LIT_DontSave") }}
                      </Button>
                      <Button
                        v-if="!hideOkBtn"
                        type="button"
                        variant="primary"
                        :data-content-action="dataContentAction"
                        :data-content-type="dataContentType"
                        :data-user-activity="dataUserActivity"
                        :busy="busy"
                        @click="onOk()"
                      >
                        {{ okBtnLabel }}
                      </Button>
                      <Button
                        v-if="showUpgradeBtn"
                        icon-start="fa-solid fa-cart-shopping"
                        variant="orange"
                        class="mx-2"
                        to="/about/pricing"
                        @click="hide"
                      >
                        {{ $t("S.LIT_Upgrade") }}
                      </Button>
                      <Button
                        v-if="!hideCancelBtn"
                        type="button"
                        class="btn btn-secondary"
                        variant="secondary"
                        @click="hide()"
                      >
                        {{ cancelBtnLabel }}
                      </Button>
                    </div>
                  </slot>
                </div>
                <div
                  v-if="$slots['footer-end']"
                  class="order-1 md:order-2"
                >
                  <slot name="footer-end" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped lang="postcss">
.btn-close {
  @apply -mb-2 text-gray-500 transition-all;
}

.btn-close:focus,
.btn-close:hover {
  @apply text-gray-800;
}

.modal-content {
  @apply grid gap-4;
}
.modal-header {
  @apply flex items-center justify-between gap-5;
}
.modal-title {
  @apply text-ccli-blue;
}
.modal-box {
  max-height: unset;
  @apply mx-auto my-auto;
}
.modal {
  @apply flex max-h-[100vh] max-h-screen items-start justify-center overflow-y-auto py-5;
}
.modal-footer-buttons {
  @apply flex flex-1 flex-wrap items-center justify-between gap-5;
}
</style>
